<template>
  <div v-if="showBanner" class="cookie-banner">
    <div class="cookie-banner__content">
      <div class="cookie-banner__main">
        <p class="cookie-banner__message">
          En cliquant sur Accepter, vous autorisez le stockage de témoins pour améliorer la navigation et analyser l’utilisation du site. Vous pouvez Paramétrer vos choix.
        </p>
        <div v-if="showSettings" class="cookie-banner__settings">
          <div class="cookie-banner__options">
            <label class="cookie-banner__option">
              <input
                  type="checkbox"
                  v-model="analyticsEnabled"
                  class="cookie-banner__checkbox"
              >
              <span class="cookie-banner__option-label">
              <strong>Google Analytics</strong>
            </span>
            </label>
          </div>
        </div>
        <div class="cookie-banner__actions">
          <button @click="savePreferences" class="btn btn-secondary btn-lg cookie-banner__button cookie-banner__button--accept">
            Accepter
          </button>
          <button @click="toggleSettings" class="btn btn-secondary btn-lg cookie-banner__button cookie-banner__button--settings">
            Paramétrer
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.cookie-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  background: white;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.2);
  animation: slideUp 0.4s ease-out;

  &__content {
    position: relative;
    z-index: 1;
    margin: 0 auto;
    padding: 20px;
  }

  &__main {
    align-items: center;
    justify-content: space-between;
    gap: 20px;

    @media (max-width: 768px) {
      flex-direction: column;
      align-items: stretch;
    }
  }

  &__message {
    flex: 1;
    margin: 0 0 20px;
    font-size: 18px;
    line-height: 1.6;
    color: #212529;
    font-family: Helvetica, Verdana, sans-serif;

    @media (max-width: 768px) {
      font-size: 13px;
      margin-bottom: 15px;
    }
  }

  &__actions {
    display: flex;
    gap: 10px;
    flex-shrink: 0;

    @media (max-width: 768px) {
      flex-direction: column;
      width: 100%;
    }
  }

  &__button {
    font-family: Helvetica, Verdana, sans-serif;
    &--accept {
      background-color: #1702f8;
      transition: background-color 0.2s ease-in-out;

      &:hover {
        background-color: #0c0280;
      }
    }

    &--settings {
      background-color: transparent;
      color: #212529;
      border: 1px solid #e0e0e0;

      &:hover {
        color: var(--bs-btn-hover-color);
        background-color: #0c0280;
      }
    }

    @media (max-width: 768px) {
      width: 100%;
      padding: 12px 24px;
    }
  }

  &__settings {
    padding: 0 0 10px 0;
  }

  &__options {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin: 0 0 15px 0;
  }

  &__option {
    display: flex;
    gap: 10px;
    cursor: pointer;
    padding: 0 10px 10px;
    border-radius: 4px;
    transition: background-color 0.2s;
    align-items: center;

    &:hover {
      background-color: rgba(255, 255, 255, 0.05);
    }
  }

  &__checkbox {
    margin-top: 2px;
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: #0000ff;
    flex-shrink: 0;
  }

  &__option-label {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;

    strong {
      font-size: 18px;
      color: #212529;
      font-family: Helvetica, Verdana, sans-serif;
    }
  }
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue';
import analyticsService from '@/services/analytics';
import {ConsentStatus, useCookieConsent} from '@/services/cookieConsent';
import { setCookie } from '@/services/handleCookies';

// Cookie consent management
const { cookieConsent, updateCookieConsent, COOKIE_CONSENT_KEY } = useCookieConsent();

// Component state
const showBanner = ref(false);
const showSettings = ref(false);
const analyticsEnabled = ref(true);

/**
 * Toggle settings panel
 */
const toggleSettings = (): void => {
  showSettings.value = !showSettings.value;

  // Update body padding after settings panel is toggled
  nextTick(() => {
    updateBodyPadding();
  });
};

/**
 * Save user preferences
 */
const savePreferences = (): void => {
  if (analyticsEnabled.value) {
    // User accepted analytics
    setCookie(COOKIE_CONSENT_KEY, ConsentStatus.ACCEPT);
    analyticsService.enable();
  } else {
    // User declined analytics
    setCookie(COOKIE_CONSENT_KEY, ConsentStatus.DECLINE);
    analyticsService.disable();
  }

  // Update the service state
  updateCookieConsent();

  // Close banner
  showBanner.value = false;
  showSettings.value = false;

  // Remove body padding
  updateBodyPadding();
};

/**
 * Update body padding when banner visibility changes
 */
const updateBodyPadding = (): void => {
  if (showBanner.value) {
    // Add padding to body to prevent banner from hiding content
    const bannerElement = document.querySelector('.cookie-banner') as HTMLElement;
    if (bannerElement) {
      // Wait for DOM update to get accurate height
      setTimeout(() => {
        const bannerHeight = bannerElement.offsetHeight;
        document.body.style.paddingBottom = `${bannerHeight}px`;
      }, 100);
    }
  } else {
    // Remove padding when banner is hidden
    document.body.style.paddingBottom = '0';
  }
};

/**
 * Check and update banner visibility based on consent
 */
const checkBannerVisibility = (): void => {
  // Update consent from cookie
  updateCookieConsent();

  const currentConsent = cookieConsent.value;

  if (currentConsent === ConsentStatus.NOT_SET) {
    // No consent given, show banner
    showBanner.value = true;
    showSettings.value = false;
    analyticsEnabled.value = true;
  } else {
    // Consent already given, hide banner and initialize analytics
    showBanner.value = false;
    showSettings.value = false;

    if (currentConsent === ConsentStatus.ACCEPT) {
      analyticsService.enable();
    } else {
      analyticsService.disable();
    }
  }

  // Update body padding after banner visibility changes
  nextTick(() => {
    updateBodyPadding();
  });
};

/**
 * Initialize component on mount
 */
onMounted(() => {
  // Initial check
  checkBannerVisibility();

  // Listen for cookie changes (e.g., when user clicks "Gérer mes cookies")
  window.addEventListener('cookie-consent-changed', checkBannerVisibility);
});

/**
 * Watch for showBanner changes to update body padding
 */
watch(showBanner, () => {
  nextTick(() => {
    updateBodyPadding();
  });
});

/**
 * Cleanup on unmount
 */
onUnmounted(() => {
  window.removeEventListener('cookie-consent-changed', checkBannerVisibility);
});
</script>
